/** Store zustand de UI: tema Ant Design (color primario y dark mode persistido), items de menú, módulos y flag de menú listo. */
import { create } from "zustand";
import { ThemeConfig } from "antd";
import { MenuItem, Module } from "@/types";
import { getLightTheme, getDarkTheme, DEFAULT_PRIMARY } from "@/themes";
import { getFromStorage, saveInStorage, removeFromStorage } from "@/utils";

const STORAGE_KEY = "innerwork-dark-mode";
const MENU_STORAGE_KEY = "innerwork-menu";
const MODULES_STORAGE_KEY = "innerwork-modules";

const getInitialDarkMode = (): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "true";
  } catch {
    return false;
  }
};

const buildTheme = (primary: string, dark: boolean): ThemeConfig => {
  return dark ? getDarkTheme(primary) : getLightTheme(primary);
};

interface UIState {
  theme: ThemeConfig;
  darkMode: boolean;
  primaryColor: string;
  currentModule: number | undefined;
  currentMenuItems: MenuItem[] | undefined;
  itemsMenu: MenuItem[];
  modules: Module[];
  menuReady: boolean;
  toggleDarkMode: () => void;
  setPrimaryColor: (color: string) => void;
  setTheme: (theme: ThemeConfig) => void;
  setCurrentModule: (module: number | undefined) => void;
  setCurrentMenuItems: (items: MenuItem[] | undefined) => void;
  setItemsMenu: (items: MenuItem[]) => void;
  setModules: (modules: Module[]) => void;
  setMenuReady: (ready: boolean) => void;
  clearMenu: () => void;
}

const initialDark = getInitialDarkMode();

export const useUIStore = create<UIState>((set) => ({
  theme: buildTheme(DEFAULT_PRIMARY, initialDark),
  darkMode: initialDark,
  primaryColor: DEFAULT_PRIMARY,
  currentModule: undefined,
  currentMenuItems: undefined,
  itemsMenu: [],
  modules: [],
  menuReady: false,

  toggleDarkMode: () =>
    set((state) => {
      const next = !state.darkMode;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return {
        darkMode: next,
        theme: buildTheme(state.primaryColor, next),
      };
    }),

  setPrimaryColor: (color) =>
    set((state) => ({
      primaryColor: color,
      theme: buildTheme(color, state.darkMode),
    })),

  setTheme: (theme) => set({ theme }),
  setCurrentModule: (module) => set({ currentModule: module }),
  setCurrentMenuItems: (items) => set({ currentMenuItems: items }),
  setItemsMenu: (items) => {
    saveInStorage(MENU_STORAGE_KEY, items);
    set({ itemsMenu: items });
  },
  setModules: (modules) => {
    saveInStorage(MODULES_STORAGE_KEY, modules);
    set({ modules });
  },
  setMenuReady: (ready) => set({ menuReady: ready }),
  clearMenu: () => {
    removeFromStorage(MENU_STORAGE_KEY);
    removeFromStorage(MODULES_STORAGE_KEY);
    set({
      itemsMenu: [],
      modules: [],
      currentModule: undefined,
      currentMenuItems: undefined,
      menuReady: false,
    });
  },
}));
